/**
 * F-148 §10.1 paused state: `canManage && !enabled`. Receipts, downloads, corrections and
 * discard keep working; drafts are read-only and create/save/import/post are blocked.
 */
import { Link } from 'react-router-dom';
import { PauseCircle, ShieldOff } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import { cn } from '@/lib/utils';

/** `!canManage` state shown on every receiving route (§10.1). */
export function ReceivingNotAvailable({ className }: { className?: string }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section
      role="status"
      data-testid="receiving-not-available"
      className={cn(
        'border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center',
        className,
      )}
    >
      <ShieldOff
        aria-hidden="true"
        className="text-muted-foreground mx-auto mb-4 size-10"
        strokeWidth={1.5}
      />
      <p className="text-foreground text-sm">{t('admin.receiving.notEnabled')}</p>
      <Link
        to="/stock"
        className="text-foreground mt-4 inline-flex min-h-11 items-center text-sm underline underline-offset-4"
      >
        {t('admin.stock.title')}
      </Link>
    </section>
  );
}

export interface ReceivingPausedBannerProps {
  /** Adds the drafts-read-only sentence (draft/list surfaces). */
  readonly draftsReadOnly?: boolean;
  /** Adds the corrections-still-allowed sentence (receipt surfaces). */
  readonly correctionsAllowed?: boolean;
  readonly className?: string;
}

export function ReceivingPausedBanner({
  draftsReadOnly = false,
  correctionsAllowed = false,
  className,
}: ReceivingPausedBannerProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section
      role="status"
      aria-live="polite"
      data-testid="receiving-paused-banner"
      className={cn(
        'flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-start text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100',
        className,
      )}
    >
      <PauseCircle aria-hidden="true" className="mt-0.5 size-5 shrink-0" strokeWidth={1.75} />
      <div className="space-y-1 text-sm">
        <p className="font-medium">{t('admin.receiving.paused.banner')}</p>
        {draftsReadOnly ? <p>{t('admin.receiving.paused.draftsReadOnly')}</p> : null}
        {correctionsAllowed ? <p>{t('admin.receiving.correction.pausedAllowed')}</p> : null}
      </div>
    </section>
  );
}
